import AsyncMessageResponseHandler from "components/Shared/AsyncMessageResponseHandler"
import Timeline from "components/Shared/Charts/Timeline"
import TimeLineSkeleton from "components/Shared/Charts/Timeline/TimelineSkeleton"
import withVisibility from "components/Shared/Hocs/withVisibility"
import SkeletonWrapper from "components/Shared/SkeletonWrapper"
import {
	CURRENT_DAYS,
	CURRENT_MONTH,
	CURRENT_QUARTER,
	CURRENT_WEEK,
	CURRENT_YEAR,
} from "helpers/temporal"
import React from "react"
import { useIntl } from "react-intl"
import ChartTooltip from "../ChartTooltip"
import {
	CURRENT_COLOR,
	PREVIOUS_COLOR,
	SLICE_TIMELINE_X,
	TIMELINE_LEGEND_HEIGHT,
	TIMELINE_LEGEND_WIDTH,
	TIMELINE_METRIC,
	day_format,
} from "../../constants"
import { LevelName, FilterLevelSelected } from "../../interfaces"
import { getMetricNameForInsight } from "../MetricsSelect/utils"
import TimeLineLegend from "../TimeLineLegend"
import { useGetTimeLineBenchmark } from "../../hooks/useGetTimeLineBenchmark"
import {
	formatInsightSubtitle,
	formatXAxis,
	formatYAxisLegend,
	generateInsightTitle,
	isIn,
	mapTimeLineResponseForChart,
	translatePresetToRange,
	getSubtitleByFilters,
} from "../../utils/utils"
import moment from "moment"
import { ExportTimeline } from "./ExportTimeline/index"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"

interface TimelineBenchmarkProps {
	filtersBenchmark: any
	setInterval: (interval: string) => void
	levelName: LevelName
	labelFatherLevelName: string
	subMetric?: string
	isPenetrationMetric?: boolean
	filterLevelSelected: FilterLevelSelected
}

const TimelineBenchmark: React.FC<TimelineBenchmarkProps> = (props) => {
	const {
		filtersBenchmark,
		levelName,
		labelFatherLevelName,
		setInterval,
		subMetric = "",
		isPenetrationMetric = false,
		filterLevelSelected,
	} = props
	const intl = useIntl()
	const {
		moduleState: {
			transactional: {
				benchmark: { metric: selectedMetric },
			},
		},
	} = useFiltersContext()
	const metric = getMetricNameForInsight(
		filtersBenchmark.metricName,
		TIMELINE_METRIC,
		subMetric
	)

	const { period: translatePeriod } = translatePresetToRange(filtersBenchmark)

	const {
		timelineData,
		isPendingRequest,
		isError,
		error,
		refetch: refetchTimeLine,
	} = useGetTimeLineBenchmark({
		...filtersBenchmark,
		metricName: metric,
		period: translatePeriod,
		selectedMetric,
	})

	const isDailyEnabled = isIn(translatePeriod, [
		CURRENT_DAYS,
		CURRENT_MONTH,
		CURRENT_QUARTER,
		CURRENT_WEEK,
	])

	const isMonthlyEnabled = isIn(translatePeriod, [
		CURRENT_QUARTER,
		CURRENT_YEAR,
	])

	const isWeeklyEnabled = isIn(translatePeriod, [
		CURRENT_MONTH,
		CURRENT_QUARTER,
		CURRENT_YEAR,
		CURRENT_WEEK,
	])

	const getReorderAndColors = () => {
		return moment(filtersBenchmark.previousFrom, day_format) >
			moment(filtersBenchmark.currentFrom, day_format)
			? { reordered: true, timelineColors: [CURRENT_COLOR, PREVIOUS_COLOR] }
			: { reordered: false, timelineColors: [PREVIOUS_COLOR, CURRENT_COLOR] }
	}

	return (
		<div data-testid="benchmark-timeline">
			<AsyncMessageResponseHandler
				hasData={Boolean(timelineData?.commercialBenchmarkTimeline?.data)}
				error={error}
				isError={isError}
				isLoading={isPendingRequest}
				retry={refetchTimeLine}
				className="benchmark-error-container"
			>
				<SkeletonWrapper
					isLoading={isPendingRequest}
					skeleton={<TimeLineSkeleton />}
				>
					<Timeline
						additionalButton={
							<ExportTimeline
								data={timelineData?.commercialBenchmarkTimeline?.data}
								metricName={filtersBenchmark.metricName}
								subMetricName={`${subMetric}`}
								intervalName={filtersBenchmark.interval}
							/>
						}
						title={generateInsightTitle(
							"phronesis.transactional.application.commercial.benchmark.timeline.title",
							levelName,
							labelFatherLevelName,
							intl,
							filtersBenchmark.metricName
						)}
						subtitle={formatInsightSubtitle(
							levelName,
							getSubtitleByFilters(filterLevelSelected, intl) ||
								labelFatherLevelName,
							filtersBenchmark.period,
							intl,
							filtersBenchmark.metricName,
							timelineData?.commercialBenchmarkTimeline.interval as string
						)}
						chartData={mapTimeLineResponseForChart(
							timelineData?.commercialBenchmarkTimeline,
							intl
						)}
						colors={getReorderAndColors().timelineColors}
						yAxisLegend={formatYAxisLegend(
							timelineData?.commercialBenchmarkTimeline,
							filtersBenchmark.metricName,
							intl,
							isPenetrationMetric
						)}
						setInterval={setInterval}
						interval={filtersBenchmark.interval as string}
						xAxisLegend={formatXAxis(filtersBenchmark.interval as string, intl)}
						contentFooter={
							<TimeLineLegend
								filtersBenchmark={filtersBenchmark}
								itemsHeight={TIMELINE_LEGEND_HEIGHT}
								itemsWidth={TIMELINE_LEGEND_WIDTH}
								timelineData={mapTimeLineResponseForChart(
									timelineData?.commercialBenchmarkTimeline,
									intl
								)}
							/>
						}
						tooltip={
							<ChartTooltip
								{...filtersBenchmark}
								{...{ reordered: getReorderAndColors().reordered }}
								{...{ isPenetrationMetric: isPenetrationMetric }}
							/>
						}
						isDailyEnabled={isDailyEnabled}
						isMonthlyEnabled={isMonthlyEnabled}
						isWeeklyEnabled={isWeeklyEnabled}
						enableGridX={false}
						enableGridY={true}
						slice={SLICE_TIMELINE_X}
						isPercentage={isPenetrationMetric}
					/>
				</SkeletonWrapper>
			</AsyncMessageResponseHandler>
		</div>
	)
}

export default withVisibility(TimelineBenchmark)
